import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import RightTimeApi from "@/API";
import { createFieldTranslator, type FieldTranslationRow } from "@/utils/fieldTranslation";

type TestTakerI18nContextValue = {
  fieldTranslations: FieldTranslationRow[];
  loading: boolean;
};

const TestTakerI18nContext = createContext<TestTakerI18nContextValue | null>(null);

type TestTakerI18nProviderProps = {
  children: ReactNode;
};

export function TestTakerI18nProvider({ children }: TestTakerI18nProviderProps) {
  const [fieldTranslations, setFieldTranslations] = useState<FieldTranslationRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadFieldTranslationsAsync() {
      setLoading(true);
      try {
        const resp = await RightTimeApi.getAsync("/FieldTranslations");
        if (!isMounted) return;

        if (resp?.ok && Array.isArray(resp.jsonData)) {
          setFieldTranslations(resp.jsonData);
        } else {
          setFieldTranslations([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void loadFieldTranslationsAsync();
    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo<TestTakerI18nContextValue>(() => ({
    fieldTranslations,
    loading,
  }), [fieldTranslations, loading]);

  return (
    <TestTakerI18nContext.Provider value={value}>
      {children}
    </TestTakerI18nContext.Provider>
  );
}

export function useOptionalTestTakerI18n(): TestTakerI18nContextValue | null {
  return useContext(TestTakerI18nContext);
}

export function useTestTakerFieldTranslator(languageId: number) {
  const context = useOptionalTestTakerI18n();
  return useMemo(
    () => createFieldTranslator(context?.fieldTranslations ?? [], languageId),
    [context?.fieldTranslations, languageId]
  );
}
